// src/Pages/PharmacistDashboard/Overview.js

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  Badge,
  Divider,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  EventNote as EventNoteIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import {
  prescriptionMockData,
  inventoryMockData,
  pharmacyMetricsMockData,
} from '../../data/pharmacistOverviewMockData';
import { Line } from 'react-chartjs-2'; // for charts

const PharmacistOverview = () => {
  const [prescriptions, setPrescriptions] = useState(prescriptionMockData);
  const [inventory, setInventory] = useState(inventoryMockData);
  const [metrics, setMetrics] = useState(pharmacyMetricsMockData);

  const handleFillPrescription = (id) => {
    setPrescriptions((prev) =>
      prev.map((prescription) =>
        prescription.id === id
          ? { ...prescription, status: 'Filled' }
          : prescription
      )
    );
  };

  const handleReorderStock = (id) => {
    setInventory((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, reordering: true } : item
      )
    );
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant='h4' gutterBottom>
        Pharmacist Dashboard Overview
      </Typography>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ marginBottom: 4 }}>
        {metrics.map((metric, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  {metric.title}
                </Typography>
                <Typography variant='h4' color='primary'>
                  {metric.value}
                </Typography>
                <Typography color='textSecondary'>
                  {metric.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ marginY: 4 }} />

      {/* Pending Prescriptions */}
      <Grid container spacing={3} sx={{ marginBottom: 4 }}>
        <Grid item xs={12}>
          <Typography variant='h6'>Pending Prescriptions</Typography>
          <Paper
            elevation={3}
            sx={{ padding: 2, maxHeight: 400, overflow: 'auto' }}
          >
            <List>
              {prescriptions
                .filter((prescription) => prescription.status === 'Pending')
                .map((prescription) => (
                  <ListItem
                    key={prescription.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <ListItemText
                      primary={`Patient: ${prescription.patient}`}
                      secondary={`Medication: ${prescription.medication} | Prescribed by: ${prescription.doctor}`}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant='contained'
                        color='primary'
                        startIcon={<CheckCircleIcon />}
                        onClick={() => handleFillPrescription(prescription.id)}
                      >
                        Mark as Filled
                      </Button>
                      <Button
                        variant='outlined'
                        color='secondary'
                        startIcon={<NotificationsIcon />}
                      >
                        Notify Patient
                      </Button>
                    </Box>
                  </ListItem>
                ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ marginY: 4 }} />

      {/* Inventory Alerts */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant='h6'>Inventory Alerts</Typography>
          <Paper
            elevation={3}
            sx={{ padding: 2, maxHeight: 400, overflow: 'auto' }}
          >
            <List>
              {inventory
                .filter((item) => item.stock < 10)
                .map((item) => (
                  <ListItem
                    key={item.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <ListItemText
                      primary={item.name}
                      secondary={`Stock: ${item.stock} | Expiry: ${item.expiry}`}
                    />
                    <IconButton color='error'>
                      <WarningIcon />
                    </IconButton>
                    <Button
                      variant='contained'
                      color='primary'
                      onClick={() => handleReorderStock(item.id)}
                      disabled={item.reordering}
                    >
                      {item.reordering ? (
                        <CircularProgress size={24} />
                      ) : (
                        'Reorder'
                      )}
                    </Button>
                  </ListItem>
                ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ marginY: 4 }} />

      {/* Medication Usage Trends (Chart) */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant='h6'>Medication Usage Trends</Typography>
          <Paper elevation={3} sx={{ padding: 3 }}>
            <Line
              data={{
                labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
                datasets: [
                  {
                    label: 'Medication Usage',
                    data: [10, 20, 30, 25],
                    fill: false,
                    borderColor: '#3f51b5',
                  },
                ],
              }}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    display: true,
                    position: 'top',
                  },
                },
              }}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PharmacistOverview;
